import type {Ref} from 'vue'
import {persistentStore, packagesStore} from '@/composables/store-accessor'
import {
    EventBus,
    FILTER_EVENT,
    SEARCH_EVENT,
    PACKAGE_FLIGHT_SELECTED,
    RESET,
    RESET_BOOKING_DATA,
    B2B_AUTH_EVENT,
    CHANGE_LOCALE_EVENT,
    CHANGE_PRICE_EVENT,
} from '@/utils/event-bus'
import {searchRequest, searchResponse} from '@/utils/packages/packages-blank-states'
import {appInstance} from '@/utils/app-accessor'
import {addSeconds} from 'date-fns'
// @ts-ignore
import {axiosInstance} from '@/utils/axios-accessor'
import {matches} from '@/utils/helpers'
import {convertPrice} from '@/utils/filters'
import {initSliderFilter} from '@/utils/store-helpers'
import {workerName} from '@/utils/worker-helpers'
import {PRODUCT_NAME as FLIGHTS_PRODUCT_NAME} from '@/utils/flights/flights-const'
import {PRODUCT_NAME as HOTELS_PRODUCT_NAME} from '@/utils/hotels/hotels-const'
import {PRODUCT_NAME} from '@/utils/packages/packages-const'

const postMessageLoad = (offers: any) => {
    console.time('filter')
    appInstance.$workers[workerName(PRODUCT_NAME)].postMessage({
        action: 'load',
        offers,
        flightSortKey: packagesStore.flightSortKey,
        hotelSortKey: packagesStore.hotelSortKey,
        flightFilters: clone(packagesStore.flightFilters),
        hotelFilters: clone(packagesStore.hotelFilters),
    })
}

export const usePackagesRuntimeStore = defineStore('packagesRuntime', () => {
    //TODO Refactoring partial search with all products
    async function _search(rq: any) {
        // @ts-ignore
        const cancelTokenSource = axiosInstance.CancelToken.source()
        SET_SEARCH_CTS(cancelTokenSource)
        const searchResponse = await appInstance.$api.searchDynamicPackages.get(rq, cancelTokenSource.token)
        if (!searchResponse) return
        if (searchResponse.offers.flights.length && searchResponse.offers.hotels.length) {
            packagesStore.SET_SEARCH_EXPIRATION_TIME(
                addSeconds(new Date(), appInstance.$conf.public.search.offersLifetime)
            )
            const hotelOfferKey = searchResponse.selectedOffersInfo.find(
                (el: any) => el.productType === 'ACCOMMODATION'
            ).offerKey
            const hotel = searchResponse.offers.hotels.find(
                ({rooms}: any) =>
                    rooms.findIndex(({groupedOffers}: any) => groupedOffers[0].offerKey === hotelOfferKey) !== -1
            )
            const flightOfferKey = searchResponse.selectedOffersInfo.find(
                (el: any) => el.productType === 'FLIGHT'
            ).offerKey
            const flight = searchResponse.offers.flights.find((el: any) => el.offerKey === flightOfferKey)
            packagesStore.SET_SELECTED_HOTEL({offerKey: hotelOfferKey, offer: hotel})
            packagesStore.SET_SELECTED_FLIGHT({offerKey: flightOfferKey, offer: flight})

            initSliderFilter(
                packagesStore.searchResponse.filters.hotels.price,
                packagesStore.hotelFilters.price,
                searchResponse.filters.hotels.price,
                'price',
                packagesStore.SET_HOTEL_FILTER
            )
            initSliderFilter(
                packagesStore.searchResponse.filters.flights.price,
                packagesStore.flightFilters.price,
                searchResponse.filters.flights.price,
                'price',
                packagesStore.SET_FLIGHT_FILTER
            )
            initSliderFilter(
                packagesStore.searchResponse.filters.flights.duration,
                packagesStore.flightFilters.duration,
                searchResponse.filters.flights.duration,
                'duration',
                packagesStore.SET_FLIGHT_FILTER
            )
        }

        const flights = searchResponse.offers.flights,
            hotels = searchResponse.offers.hotels,
            sr = {...searchResponse}
        sr.offers.flightsCount = flights.length
        sr.offers.hotelsCount = hotels.length
        packagesStore.SET_SEARCH_RESPONSE(sr)
        appInstance.$localForage.setItem(PRODUCT_NAME, {flights, hotels})
        postMessageLoad({flights, hotels})
        EventBus.$emit(PACKAGE_FLIGHT_SELECTED)
    }

    function _newSearch(rq: any) {
        stopSearch()
        SET_FLIGHT_OFFERS([])
        SET_HOTEL_OFFERS([])
        packagesStore.SET_SEARCH_RESPONSE(searchResponse())
        packagesStore.NEW_SEARCH(rq)
    }

    const arrivalPoint: Ref = ref({})
    const departurePoint: Ref = ref({})

    const updateActiveOffers: Ref<any[]> = ref([])
    const searchActiveCount = ref(0)
    const filterActiveCount = ref(0)
    const searchCTS: Ref = ref(null)
    const flightOffers: Ref<any[]> = ref([])
    const hotelOffers: Ref<any[]> = ref([])

    function SET_ARRIVAL_POINT(val: any) {
        arrivalPoint.value = val
    }

    function SET_DEPARTURE_POINT(val: any) {
        departurePoint.value = val
    }

    function SET_OFFERS(val: any) {
        if (val.flights && val.hotels) {
            flightOffers.value = val.flights
            hotelOffers.value = val.hotels
        } else {
            flightOffers.value = []
            hotelOffers.value = []
        }
    }

    function SET_FLIGHT_OFFERS(val: any) {
        flightOffers.value = val
    }

    function SET_HOTEL_OFFERS(val: any) {
        hotelOffers.value = val
    }

    function START_SEARCH() {
        searchActiveCount.value++
    }

    function STOP_SEARCH() {
        searchActiveCount.value--
    }

    function START_FILTER() {
        filterActiveCount.value++
    }

    function STOP_FILTER() {
        filterActiveCount.value--
    }

    function SET_SEARCH_CTS(cancelTokenSource: any) {
        searchCTS.value = cancelTokenSource
    }

    function ADD_UPDATE_ROOMS_ACTIVE({supplierCode, cityCode, hotelCode}: any) {
        updateActiveOffers.value.push({supplierCode, cityCode, hotelCode})
    }

    function REMOVE_UPDATE_ROOMS_ACTIVE({supplierCode, cityCode, hotelCode}: any) {
        updateActiveOffers.value = updateActiveOffers.value.filter(
            item => !matches(item, {supplierCode, cityCode, hotelCode})
        )
    }

    function UPDATE_ROOMS({supplierCode, cityCode, hotelCode, rooms}: any) {
        const hotel = hotelOffers.value.find(
            hotel => hotel.supplierCode === supplierCode && hotel.cityCode === cityCode && hotel.hotelCode === hotelCode
        )
        hotel.rooms = rooms
        hotel.updatedRooms = true
    }

    async function loadPoints({departureCityId, arrivalCityId}: any) {
        try {
            const points = await Promise.all([
                appInstance.$api.locations.get({id: departureCityId, limitCities: 1}),
                appInstance.$api.locations.get({id: arrivalCityId, limitCities: 1}),
            ])
            departurePoint.value = points[0].cities[0]
            arrivalPoint.value = points[1].cities[0]
        } catch (e) {
            departurePoint.value = null
            arrivalPoint.value = null
        }
    }

    async function loadArrivalPoint(arrivalCityId: any) {
        try {
            const {
                cities: [_arrivalPoint],
            } = await appInstance.$api.locations.get({id: arrivalCityId, limitCities: 1})

            arrivalPoint.value = _arrivalPoint
        } catch (e) {
            arrivalPoint.value = {}
        }
    }

    function clientInit() {
        EventBus.$on(RESET, reset)
        EventBus.$on(RESET_BOOKING_DATA, reset)
        EventBus.$on(B2B_AUTH_EVENT, reset)
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        EventBus.$on(CHANGE_PRICE_EVENT, changePrice)
        appInstance.$workers[workerName(PRODUCT_NAME)].onmessage = ({
            data: {
                action,
                offers: {flights, hotels},
            },
        }: any) => {
            console.timeEnd('filter')
            STOP_FILTER()
            if (action === 'load') {
                SET_HOTEL_OFFERS(hotels)
                SET_FLIGHT_OFFERS(flights)
            } else if (action === FLIGHTS_PRODUCT_NAME) {
                if (filterActive.value) return
                SET_FLIGHT_OFFERS(flights)
            } else if (action === HOTELS_PRODUCT_NAME) {
                if (filterActive.value) return
                SET_HOTEL_OFFERS(hotels)
            }
            EventBus.$emit(FILTER_EVENT)
        }
    }

    async function changePrice({offerKey, prepareBookResponse}: any) {
        packagesStore.REFRESH_BASKET_PRICE({offerKey, prepareBookResponse})
        persistentStore.REFRESH_CONDITIONS({offerKey, prepareBookResponse})
    }

    async function reload() {
        if (arrivalPoint.value.id && departurePoint.value.id)
            await loadPoints({departureCityId: departurePoint.value.id, arrivalCityId: arrivalPoint.value.id})
    }

    function reset() {
        _newSearch(searchRequest())
        packagesStore.RESET()
    }

    function newSearch() {
        _newSearch(searchRequest())
    }

    function stopSearch() {
        if (searchCTS.value) searchCTS.value.cancel()
        SET_SEARCH_CTS(null)
    }

    async function search(rq: any) {
        START_SEARCH()
        _newSearch(rq)
        EventBus.$emit(SEARCH_EVENT)
        try {
            await _search(rq)
            // eslint-disable-next-line no-empty
        } catch (e) {
        } finally {
            STOP_SEARCH()
        }
    }

    /**
     * For TBO supplier - hotel room offers refresh
     * @param supplierCode
     * @param cityCode
     * @param hotelCode
     * @param offerId
     * @returns {Promise<void>}
     */
    async function updatePackageOffers({supplierCode, cityCode, hotelCode, offerId}: any) {
        try {
            ADD_UPDATE_ROOMS_ACTIVE({supplierCode, cityCode, hotelCode})
            const rs = await appInstance.$api.updatePackageOffers.put({
                offersId: {
                    type: 'ACCOMMODATION',
                    offerId,
                },
                offerKey: packagesStore.searchResponse.offerKey,
            })
            const rooms = rs.offers.hotels.find(
                (hotel: any) =>
                    hotel.supplierCode === supplierCode && hotel.cityCode === cityCode && hotel.hotelCode === hotelCode
            ).rooms
            appInstance.$workers[workerName(PRODUCT_NAME)].postMessage({
                action: 'refreshRooms',
                rooms,
                hotel: {supplierCode, cityCode, hotelCode},
            })
            UPDATE_ROOMS({supplierCode, cityCode, hotelCode, rooms})
        } catch (e) {
            return e
        } finally {
            REMOVE_UPDATE_ROOMS_ACTIVE({supplierCode, cityCode, hotelCode})
        }
    }

    async function load() {
        START_FILTER()
        console.time('filter')
        const offers = await appInstance.$localForage.getItem(PRODUCT_NAME)
        appInstance.$workers[workerName(PRODUCT_NAME)].postMessage({
            action: 'load',
            offers,
            flightSortKey: packagesStore.flightSortKey,
            hotelSortKey: packagesStore.hotelSortKey,
            flightFilters: clone(packagesStore.flightFilters),
            hotelFilters: clone(packagesStore.hotelFilters),
        })
    }

    async function filterFlights() {
        START_FILTER()
        console.time('filter')
        appInstance.$workers[workerName(PRODUCT_NAME)].postMessage({
            action: FLIGHTS_PRODUCT_NAME,
            flightFilters: clone(packagesStore.flightFilters),
            flightSortKey: packagesStore.flightSortKey,
        })
    }

    async function filterHotels() {
        START_FILTER()
        console.time('filter')
        appInstance.$workers[workerName(PRODUCT_NAME)].postMessage({
            action: HOTELS_PRODUCT_NAME,
            hotelFilters: clone(packagesStore.hotelFilters),
            hotelSortKey: packagesStore.hotelSortKey,
        })
    }

    const searchActive = computed(() => {
        return searchActiveCount.value > 0
    })

    const filterActive = computed(() => {
        return filterActiveCount.value > 0
    })

    const searchPageLink = computed(() => {
        return (searchRequest: any) => {
            const {convertToCurrency, ...query} = searchRequest
            return {name: 'packages', query}
        }
    })

    const totalPrice = computed(() => {
        return (room: any, flight: any, price: any) => ({
            amount:
                convertPrice(price).amount +
                convertPrice(room.deltaPrice).amount +
                convertPrice(flight.deltaPrice).amount,
            currency: persistentStore.currency,
        })
    })

    const updateRoomsActive = computed(() => {
        return ({supplierCode, cityCode, hotelCode}: any) =>
            updateActiveOffers.value.findIndex(item => matches(item, {supplierCode, cityCode, hotelCode})) !== -1
    })

    return {
        arrivalPoint,
        departurePoint,
        updateActiveOffers,
        searchActiveCount,
        filterActiveCount,
        searchCTS,
        flightOffers,
        hotelOffers,
        SET_ARRIVAL_POINT,
        SET_DEPARTURE_POINT,
        SET_OFFERS,
        SET_FLIGHT_OFFERS,
        SET_HOTEL_OFFERS,
        START_SEARCH,
        STOP_SEARCH,
        START_FILTER,
        STOP_FILTER,
        SET_SEARCH_CTS,
        ADD_UPDATE_ROOMS_ACTIVE,
        REMOVE_UPDATE_ROOMS_ACTIVE,
        UPDATE_ROOMS,
        loadPoints,
        loadArrivalPoint,
        clientInit,
        changePrice,
        reload,
        reset,
        newSearch,
        stopSearch,
        search,
        updatePackageOffers,
        load,
        filterFlights,
        filterHotels,
        searchActive,
        filterActive,
        searchPageLink,
        totalPrice,
        updateRoomsActive,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(usePackagesRuntimeStore, import.meta.hot))
